import React, { useState } from "react";
import { toast } from "react-toastify";
import { FaTimes } from "react-icons/fa";
import ClipLoader from "react-spinners/ClipLoader";
import api from "../../utils/api";

const COLORS = {
  primary: "#f66061",
};

const inputClass =
  "w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-[#f66061] focus:border-transparent transition text-sm";

const labelClass = "block mb-2 text-gray-700 font-semibold text-sm";

const EditCouponModal = ({ coupon, onClose, onUpdated }) => {
  const [code, setCode] = useState(coupon.code);
  const [discount, setDiscount] = useState(coupon.discountPercentage);
  const [applicableCards, setApplicableCards] = useState(coupon.applicableCards);
  const [isActive, setIsActive] = useState(coupon.isActive);
  const [loading, setLoading] = useState(false);

  const handleUpdate = async () => {
    if (!code || !discount || applicableCards < 1) {
      toast.error("Please fill all fields correctly");
      return;
    }

    setLoading(true);
    try {
      const res = await api.put(`/coupon/edit/${coupon._id}`, {
        code,
        discountPercentage: Number(discount),
        applicableCards: Number(applicableCards),
        isActive,
      });

      if (res.data.code === 200) {
        toast.success("Coupon updated!");
        onUpdated(res.data.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update coupon");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800">Edit Coupon</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition p-1 rounded"
          >
            <FaTimes size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          <div>
            <label className={labelClass}>Coupon Code</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className={`${inputClass} font-mono tracking-widest`}
              placeholder="e.g. NEWYEAR2026"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Discount %</label>
              <input
                type="number"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className={inputClass}
                min={1}
                max={100}
                placeholder="1–100"
              />
            </div>

            <div>
              <label className={labelClass}>Applicable Cards</label>
              <input
                type="number"
                value={applicableCards}
                onChange={(e) => setApplicableCards(e.target.value)}
                className={inputClass}
                min={1}
                placeholder="Min 1"
              />
            </div>
          </div>

          {/* Toggle Status */}
          <div>
            <label className={labelClass}>Status</label>
            <button
              type="button"
              onClick={() => setIsActive((prev) => !prev)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f66061] ${
                isActive ? "bg-[#f66061]" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  isActive ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span className="ml-3 text-sm text-gray-600 font-medium">
              {isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-700 transition text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={loading}
            style={{ backgroundColor: COLORS.primary }}
            className="text-white px-5 py-2 rounded hover:opacity-90 transition text-sm font-medium flex items-center justify-center min-w-[90px]"
          >
            {loading ? <ClipLoader size={16} color="#fff" /> : "Update Coupon"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCouponModal;